import { useEffect, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { useConsent } from '../consent/ConsentContext';
import { DEFAULT_CATEGORIES, COOKIE_INVENTORY } from '../consent/consent-storage';
import type { ConsentCategory, ConsentDecision } from '../consent/types';

const TOGGLABLE: Array<Exclude<ConsentCategory, 'necessary'>> = ['functional', 'analytics', 'marketing'];

export function ConsentModal() {
  const { t } = useTranslation();
  const { isModalOpen, closeModal, categories, acceptAll, rejectAll, saveCustom, hasDecision } =
    useConsent();

  const [draft, setDraft] = useState<Record<ConsentCategory, ConsentDecision>>(
    hasDecision ? categories : DEFAULT_CATEGORIES
  );

  useEffect(() => {
    if (isModalOpen) {
      setDraft(hasDecision ? categories : DEFAULT_CATEGORIES);
    }
  }, [isModalOpen, hasDecision, categories]);

  const toggle = (cat: ConsentCategory) => {
    setDraft((d) => ({ ...d, [cat]: d[cat] === 'granted' ? 'denied' : 'granted' }));
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('cookies.modal.title')}</DialogTitle>
          <DialogDescription>{t('cookies.modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
          <CategoryRow
            category="necessary"
            checked
            disabled
            onToggle={() => {}}
            examplesLabel={t('cookies.categories.necessary.alwaysOn')}
          />
          {TOGGLABLE.map((cat) => (
            <CategoryRow
              key={cat}
              category={cat}
              checked={draft[cat] === 'granted'}
              onToggle={() => toggle(cat)}
              examplesLabel={COOKIE_INVENTORY[cat]
                .slice(0, 3)
                .map((i) => i.name)
                .join(', ')}
            />
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={rejectAll}>
            {t('cookies.modal.rejectAll')}
          </Button>
          <Button variant="outline" onClick={acceptAll}>
            {t('cookies.modal.acceptAll')}
          </Button>
          <Button onClick={() => saveCustom(draft)}>{t('cookies.modal.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryRowProps {
  category: ConsentCategory;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
  examplesLabel?: string;
}

const CategoryRow: FC<CategoryRowProps> = ({ category, checked, disabled, onToggle, examplesLabel }) => {
  const { t } = useTranslation();
  return (
    <label
      className="flex items-start gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-colors has-disabled:cursor-default has-disabled:hover:bg-transparent"
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={() => !disabled && onToggle()}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{t(`cookies.categories.${category}.title`)}</div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {t(`cookies.categories.${category}.description`)}
        </div>
        {examplesLabel && (
          <div className="text-[11px] text-zinc-400 mt-1 font-mono truncate">{examplesLabel}</div>
        )}
      </div>
    </label>
  );
};
